import { Box, Button, Chip, Icon, Paper, Tooltip } from "@mui/material";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { AuthContext } from "~/auth/AuthProvider";
import useMyUser from "~/auth/useMyUser";
import FormModal from "~/components/FormModal";
import { BodyTextSmall, CaptionText, TitleText } from "~/components/Typographies";
import UserAvatar from "~/components/UserAvatar";
import Config from "~/config";
import logger from "~/helpers/logger";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import useOutlookMailboxConnect from "~/hooks/useOutlookMailboxConnect";
import { useSnacks } from "~/providers/SnackProvider";
import { useGetTenantQuery } from "~/services/endpoints/apiEndpoints";
import { closeProfilePanel, openPreferencesModal } from "~/store/layoutSlice";
import { selectStoragePersisted } from "~/store/storageDurabilitySlice";
import { selectSyncActivityRecords } from "~/store/syncActivitySlice";
import { selectSyncMode } from "~/store/syncModeSlice";
import { withoutRetiredRoles } from "~admin/users/helpers/userRoleDisplay";
import ProfilePictureSection from "~settings/profile/components/ProfilePictureSection";
import ProfileSectionCard from "~settings/profile/components/ProfileSectionCard";
import ProfileValueTile from "~settings/profile/components/ProfileValueTile";
import formatUserRole from "~settings/profile/helpers/formatUserRole";
import linkedEmailAccountStatus from "~settings/profile/helpers/linkedEmailAccountStatus";
import formatSyncModeLabel, {
	formatLastBackgroundSyncLabel,
	formatStoragePersistedLabel,
	formatSyncRegistrationLabel
} from "~settings/profile/helpers/profileDiagnosticsLabels";
import requestPasswordReset from "~settings/profile/helpers/requestPasswordReset";
import resolveUserDisplayName from "~settings/profile/helpers/resolveUserDisplayName";
import stripCompanyShortCode from "~settings/profile/helpers/stripCompanyShortCode";

interface ProfileProps {
	hosted?: boolean;
}

const tileRowSx = { display: "flex", flexWrap: "wrap", gap: 1.5 } as const;

const Profile = ({ hosted = false }: ProfileProps) => {
	const { t } = useTranslation("common");
	const { currentUser } = useContext(AuthContext);
	const dispatch = useAppDispatch();
	const { myUser } = useMyUser();
	const { email } = useAppSelector((state) => state.settings);
	const { mode: syncMode, reason: syncModeReason } = useAppSelector(selectSyncMode);
	const syncActivityRecords = useAppSelector(selectSyncActivityRecords);
	const storagePersisted = useAppSelector(selectStoragePersisted);
	const { data: tenantData } = useGetTenantQuery();
	const [openSnack] = useSnacks();
	const [isSendingReset, setIsSendingReset] = useState(false);
	const [isPictureModalOpen, setIsPictureModalOpen] = useState(false);
	const { isLoading: isMailboxStatusLoading, status: mailboxStatus } = useOutlookMailboxConnect();

	const resolvedEmail = myUser?.email ?? email ?? "";
	const realName = resolveUserDisplayName(myUser, currentUser);
	const companyName = stripCompanyShortCode(tenantData?.tenant?.name);
	const roles = withoutRetiredRoles(myUser?.roles ?? []);
	const emailAccountStatus = linkedEmailAccountStatus(
		mailboxStatus?.connected ?? false,
		mailboxStatus?.mailboxAddress,
		mailboxStatus?.grantsSendScope ?? false
	);

	const goToLinkedEmailAccount = () => {
		dispatch(closeProfilePanel());
		dispatch(openPreferencesModal("linked-email"));
	};

	const handleChangePassword = async () => {
		if (!resolvedEmail) {
			openSnack("We couldn't find your email address.", "error");
			return;
		}
		setIsSendingReset(true);
		try {
			await requestPasswordReset(resolvedEmail);
			openSnack("We've emailed you a reset link.", "success");
		} catch (error) {
			logger.error("Password reset request failed", error);
			openSnack("We couldn't send a reset link. Please try again.", "error");
		} finally {
			setIsSendingReset(false);
		}
	};

	const content = (
		<>
			<ProfileSectionCard defaultExpanded icon="person" title="User Info">
				<Box sx={{ alignItems: "center", display: "flex", gap: 2, mb: 2 }}>
					<UserAvatar imageUrl={myUser?.profileImageUrl} name={realName ?? resolvedEmail} size={56} />
					<Button
						color="primary"
						onClick={() => setIsPictureModalOpen(true)}
						startIcon={<Icon>photo_camera</Icon>}
						variant="outlined"
					>
						Change Photo
					</Button>
				</Box>
				<Box sx={tileRowSx}>
					{realName && <ProfileValueTile label="Name" value={realName} />}
					<ProfileValueTile label="Email" value={resolvedEmail || "—"} />
					{companyName && <ProfileValueTile label="Tenant" value={companyName} />}
				</Box>
			</ProfileSectionCard>

			<FormModal onClose={() => setIsPictureModalOpen(false)} open={isPictureModalOpen} title="Profile Picture">
				<ProfilePictureSection displayName={realName ?? resolvedEmail} imageUrl={myUser?.profileImageUrl} />
			</FormModal>

			<ProfileSectionCard icon="badge" title="Roles">
				{roles.length > 0 ? (
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
						{roles.map((role) => (
							<Chip key={role} label={formatUserRole(role)} size="small" variant="outlined" />
						))}
					</Box>
				) : (
					<BodyTextSmall color="text.secondary">No roles assigned.</BodyTextSmall>
				)}
			</ProfileSectionCard>

			<ProfileSectionCard icon="lock" title="Security">
				<Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "space-between" }}>
					<BodyTextSmall color="text.secondary" sx={{ minWidth: 0 }}>
						Reset your password by email.
					</BodyTextSmall>
					<Button
						color="primary"
						loading={isSendingReset}
						onClick={handleChangePassword}
						startIcon={<Icon>lock_reset</Icon>}
						variant="outlined"
					>
						Change Password
					</Button>
				</Box>
			</ProfileSectionCard>

			<ProfileSectionCard icon="mail" title="Linked Email Account">
				<Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "space-between" }}>
					<BodyTextSmall color="text.secondary" sx={{ minWidth: 0 }}>
						Email account: {isMailboxStatusLoading ? "Checking..." : emailAccountStatus}
					</BodyTextSmall>
					<Button
						color="primary"
						onClick={goToLinkedEmailAccount}
						size="small"
						startIcon={<Icon>settings</Icon>}
						variant="outlined"
					>
						Manage
					</Button>
				</Box>
			</ProfileSectionCard>

			{currentUser && (
				<ProfileSectionCard icon="hub" title="Current Connection">
					<Box sx={tileRowSx}>
						<Tooltip title={Config.endpoint}>
							<Box sx={{ maxWidth: "100%", minWidth: 0 }}>
								<ProfileValueTile label="Endpoint" mono value={Config.endpoint} />
							</Box>
						</Tooltip>
						<ProfileValueTile label="Tenant" value={currentUser.wsapiTenant} />
					</Box>
				</ProfileSectionCard>
			)}

			<ProfileSectionCard defaultExpanded icon="info" title="Version">
				<Box sx={tileRowSx}>
					<ProfileValueTile label="Build" value={Config.buildVersion} />
					<ProfileValueTile label="Release" value={Config.releaseVersion} />
					<ProfileValueTile
						label={t("profile.version.backgroundSync")}
						value={formatSyncModeLabel(syncMode, syncModeReason)}
					/>
					<ProfileValueTile
						label={t("profile.version.storage")}
						value={formatStoragePersistedLabel(storagePersisted)}
					/>
					<ProfileValueTile
						label={t("profile.version.lastBackgroundSync")}
						value={formatLastBackgroundSyncLabel(syncActivityRecords)}
					/>
					<ProfileValueTile
						label={t("profile.version.syncRegistration")}
						value={formatSyncRegistrationLabel(syncActivityRecords)}
					/>
				</Box>
				<CaptionText sx={{ color: "text.secondary", display: "block", mt: 1.5, wordBreak: "break-word" }}>
					{navigator.userAgent}
				</CaptionText>
			</ProfileSectionCard>
		</>
	);

	if (hosted) return content;

	return (
		<Paper elevation={0} sx={{ p: 3 }}>
			<TitleText bold sx={{ mb: 3 }}>
				Profile
			</TitleText>
			{content}
		</Paper>
	);
};

export default Profile;
